import { useEffect, useState } from 'react';
import { Box, HStack, Spacer, Switch, Text, VStack, useToast } from '@chakra-ui/react';
import { Button, ConfirmDialog, Modal, SegmentedControl, TextField } from '../../../../ui';
import type { UserPermission } from '../../../../hooks/useEditDefaultPermissions';
import InvitePermissionsEditor from '../users/addUsers/InvitePermissionsEditor';
import {
    PERMISSIONS,
    TABS,
    copyUserPermission,
    sameUserPermission,
} from '../permissions/permissionCatalog';
import InviteHandoffModal, { inviteToastOptions, needsHandoff, type InviteHandoff } from './InviteHandoffModal';
import {
    teamErrorMessage,
    useInviteEmployee,
    useUpdateEmployee,
    type EmployeeAccess,
    type YaxleTeamMember,
} from './useYaxleTeam';

type AccessMode = 'admin' | 'custom';
type InviteField = 'email' | 'firstName' | 'lastName';

/** Every tab and permission, since an employee's access applies in every org. The API has no Tasks tab yet. */
const EMPLOYEE_MATRIX_ROWS = {
    tabs: TABS.filter((tab) => tab !== 'tasks'),
    permissions: PERMISSIONS,
};

const COMPANY_EMAIL = /^[^\s@]+@(yaxle\.com|partbay\.ai)$/i;

const ACCESS_DESCRIPTIONS: Record<AccessMode, string> = {
    admin: 'Everything in every organization, including admin-only settings like locations and connections.',
    custom: 'Only the tabs and permissions checked below, in every organization. Each organization still hides what it has turned off.',
};

const sameAccess = (a: EmployeeAccess, b: EmployeeAccess) =>
    a === 'admin' || b === 'admin' ? a === b : sameUserPermission(a, b);

export interface EmployeeAccessModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** The employee to edit; omit to invite someone new. */
    member?: YaxleTeamMember;
    microsoft365Provisioning?: boolean;
}

/**
 * Invite an employee, or change one's access, team-manager flag, and status.
 * Access is "Admin" or a custom set of tabs and permissions; it applies in
 * every org the employee opens. An invite that leaves something to hand over
 * (a new Microsoft 365 password, an unsent link) ends on the handoff view.
 */
const EmployeeAccessModal = ({ isOpen, onClose, member, microsoft365Provisioning = false }: EmployeeAccessModalProps) => {
    const toast = useToast();
    const inviteEmployee = useInviteEmployee();
    const updateEmployee = useUpdateEmployee();
    const isInvite = !member;

    const [fields, setFields] = useState<Record<InviteField, string>>({ email: '', firstName: '', lastName: '' });
    const [errors, setErrors] = useState<Partial<Record<InviteField, string>>>({});
    const [mode, setMode] = useState<AccessMode>('custom');
    const [custom, setCustom] = useState<UserPermission>(() => copyUserPermission());
    const [canManageTeam, setCanManageTeam] = useState(false);
    const [isConfirmingStatus, setIsConfirmingStatus] = useState(false);
    const [handoff, setHandoff] = useState<InviteHandoff | null>(null);

    useEffect(() => {
        if (!isOpen) return;
        setFields({ email: '', firstName: '', lastName: '' });
        setErrors({});
        setMode(member?.access === 'admin' ? 'admin' : 'custom');
        setCustom(copyUserPermission(member && member.access !== 'admin' ? member.access : undefined));
        setCanManageTeam(member?.canManageTeam ?? false);
        setIsConfirmingStatus(false);
        setHandoff(null);
    }, [isOpen, member]);

    const access: EmployeeAccess = mode === 'admin' ? 'admin' : custom;
    const isSaving = inviteEmployee.isPending || updateEmployee.isPending;

    const editField = (field: InviteField, value: string) => {
        setFields((previous) => ({ ...previous, [field]: value }));
        setErrors((previous) => ({ ...previous, [field]: undefined }));
    };

    const handleInvite = () => {
        const email = fields.email.trim().toLowerCase();
        const firstName = fields.firstName.trim();
        const lastName = fields.lastName.trim();
        const nextErrors: Partial<Record<InviteField, string>> = {
            ...(!COMPANY_EMAIL.test(email) ? { email: 'Use their @yaxle.com or @partbay.ai address.' } : {}),
            ...(!firstName ? { firstName: 'Required' } : {}),
            ...(!lastName ? { lastName: 'Required' } : {}),
        };
        setErrors(nextErrors);
        if (Object.keys(nextErrors).length > 0) return;
        inviteEmployee.mutate(
            { email, firstName, lastName, access, canManageTeam },
            {
                onSuccess: (result) => {
                    if (needsHandoff(result)) {
                        setHandoff({ email, name: firstName, result });
                        return;
                    }
                    toast(inviteToastOptions(email, result));
                    onClose();
                },
                onError: (error) =>
                    toast({
                        status: 'error',
                        title: "Couldn't send the invite",
                        description: teamErrorMessage(error, 'Try again in a moment.'),
                        duration: 8000,
                        isClosable: true,
                    }),
            },
        );
    };

    const handleSave = () => {
        if (!member) return;
        const changes = {
            ...(!sameAccess(access, member.access) ? { access } : {}),
            ...(canManageTeam !== member.canManageTeam ? { canManageTeam } : {}),
        };
        if (Object.keys(changes).length === 0) {
            onClose();
            return;
        }
        updateEmployee.mutate(
            { userId: member.id, ...changes },
            {
                onSuccess: () => {
                    toast({
                        status: 'success',
                        title: `Saved ${member.firstName}'s access`,
                        description: 'Open sessions pick it up within 5 minutes.',
                        duration: 5000,
                        isClosable: true,
                    });
                    onClose();
                },
                onError: (error) =>
                    toast({
                        status: 'error',
                        title: "Couldn't save",
                        description: teamErrorMessage(error, 'Try again in a moment.'),
                        isClosable: true,
                    }),
            },
        );
    };

    const handleToggleActive = () => {
        if (!member) return;
        updateEmployee.mutate(
            { userId: member.id, active: !member.active },
            {
                onSuccess: () => {
                    toast({
                        status: 'success',
                        title: member.active ? `${member.firstName} is deactivated` : `${member.firstName} is active again`,
                        description: member.active ? 'Their open sessions end within 5 minutes.' : undefined,
                        isClosable: true,
                    });
                    setIsConfirmingStatus(false);
                    onClose();
                },
                onError: (error) =>
                    toast({
                        status: 'error',
                        title: "Couldn't change their status",
                        description: teamErrorMessage(error, 'Try again in a moment.'),
                        isClosable: true,
                    }),
            },
        );
    };

    if (isOpen && handoff) {
        return <InviteHandoffModal handoff={handoff} onClose={onClose} />;
    }

    const title = isInvite ? 'Invite employee' : `${member.firstName} ${member.lastName}`.trim() || member.email;

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                size="2xl"
                title={title}
                footer={
                    <>
                        {member && (
                            <Button
                                variant={member.active ? 'outline' : 'outlineGray'}
                                colorScheme={member.active ? 'red' : undefined}
                                onClick={() => (member.active ? setIsConfirmingStatus(true) : handleToggleActive())}
                                isDisabled={member.isYou || isSaving}
                            >
                                {member.active ? 'Deactivate' : 'Reactivate'}
                            </Button>
                        )}
                        <Spacer />
                        <Button variant="ghostGray" onClick={onClose} isDisabled={isSaving}>
                            Cancel
                        </Button>
                        <Button onClick={isInvite ? handleInvite : handleSave} isLoading={isSaving}>
                            {isInvite ? 'Send invite' : 'Save'}
                        </Button>
                    </>
                }
            >
                <VStack align="stretch" spacing={6} pb={2}>
                    {isInvite ? (
                        <VStack align="stretch" spacing={4}>
                            <HStack spacing={4} align="flex-start">
                                <TextField
                                    label="First name"
                                    value={fields.firstName}
                                    onChange={(event) => editField('firstName', event.target.value)}
                                    error={errors.firstName}
                                    autoFocus
                                />
                                <TextField
                                    label="Last name"
                                    value={fields.lastName}
                                    onChange={(event) => editField('lastName', event.target.value)}
                                    error={errors.lastName}
                                />
                            </HStack>
                            <TextField
                                label="Work email"
                                type="email"
                                placeholder="name@yaxle.com"
                                value={fields.email}
                                onChange={(event) => editField('email', event.target.value)}
                                error={errors.email}
                                helperText={
                                    microsoft365Provisioning
                                        ? "If they don't have a Microsoft 365 account yet, this creates one with the employee license and group."
                                        : 'They get an email with a link to create their account.'
                                }
                            />
                        </VStack>
                    ) : (
                        <Text textStyle="bodySm" color="fg.muted">
                            {member.email}
                        </Text>
                    )}

                    <Box>
                        <HStack justify="space-between" mb={2}>
                            <Text textStyle="labelSm" color="fg.default">
                                Access
                            </Text>
                            <SegmentedControl<AccessMode>
                                items={[
                                    { value: 'admin', label: 'Admin' },
                                    { value: 'custom', label: 'Custom' },
                                ]}
                                value={mode}
                                onChange={setMode}
                            />
                        </HStack>
                        <Text textStyle="bodySm" color="fg.muted" mb={mode === 'custom' ? 3 : 0}>
                            {ACCESS_DESCRIPTIONS[mode]}
                        </Text>
                        {mode === 'custom' && (
                            <InvitePermissionsEditor
                                value={custom}
                                onChange={setCustom}
                                matrixRows={EMPLOYEE_MATRIX_ROWS}
                            />
                        )}
                    </Box>

                    <HStack justify="space-between" align="flex-start" spacing={6}>
                        <Box>
                            <Text textStyle="labelSm" color="fg.default">
                                Can manage the Yaxle team
                            </Text>
                            <Text textStyle="bodySm" color="fg.muted">
                                {member?.isYou
                                    ? 'Another team manager has to change this for you.'
                                    : 'Can invite employees and change anyone’s access on this page.'}
                            </Text>
                        </Box>
                        <Switch
                            size="lg"
                            colorScheme="brand"
                            isChecked={canManageTeam}
                            isDisabled={member?.isYou}
                            onChange={(event) => setCanManageTeam(event.target.checked)}
                            aria-label="Can manage the Yaxle team"
                        />
                    </HStack>
                </VStack>
            </Modal>

            {member && (
                <ConfirmDialog
                    isOpen={isConfirmingStatus}
                    onClose={() => setIsConfirmingStatus(false)}
                    onConfirm={handleToggleActive}
                    title={`Deactivate ${member.firstName}?`}
                    confirmLabel="Deactivate"
                    confirmColorScheme="red"
                    isConfirmLoading={updateEmployee.isPending}
                >
                    They can’t log in until a team manager reactivates them, and their open sessions end within 5
                    minutes. Their Microsoft 365 account isn’t changed.
                </ConfirmDialog>
            )}
        </>
    );
};

export default EmployeeAccessModal;
